import { TFunction } from 'i18next';
import { AIActionKind, AIInlineActionTemplate } from 'models/copilot/actions/aiActionTemplate';

/**
 * One criteria action of one entity. A form can serve several entities, so every entity gets its
 * own binding (the anchor) and the label carries the entity name when there is more than one.
 */
export type CriteriaActionParams = {
  entityType: number;
  entityId: number;
  entityName: string;
  withEntityName: boolean;
  run: () => void;
};

const getCriteriaAnchor = (params: CriteriaActionParams): string => `${params.entityType}:${params.entityId}`;

const getCriteriaLabel = (label: string, params: CriteriaActionParams): string =>
  params.withEntityName ? `${label} - ${params.entityName}` : label;

// Re-evaluates the criteria against their current sources: keeps, rewords, adds and archives.
export const criteriaReevaluateTemplate: AIInlineActionTemplate<CriteriaActionParams> = {
  id: 'criteria.reevaluate',
  kind: AIActionKind.Inline,
  icon: { iconName: 'Sync' },
  getLabel(t: TFunction<string[]>, params) {
    return getCriteriaLabel(t('criteria:Editor.Reevaluate'), params);
  },
  getAnchor(params) {
    return getCriteriaAnchor(params);
  },
  run(params) {
    params.run();
  },
};

// Generates the criteria afresh.
export const criteriaGenerateTemplate: AIInlineActionTemplate<CriteriaActionParams> = {
  id: 'criteria.generate',
  kind: AIActionKind.Inline,
  icon: { iconName: 'Refresh' },
  getLabel(t: TFunction<string[]>, params) {
    return getCriteriaLabel(t('criteria:Editor.UpdateGenerate'), params);
  },
  getAnchor(params) {
    return getCriteriaAnchor(params);
  },
  run(params) {
    params.run();
  },
};

// Advises evidence tasks through the control audit's evidence inspection; controls only.
export const criteriaAdviseEvidenceTemplate: AIInlineActionTemplate<CriteriaActionParams> = {
  id: 'criteria.adviseEvidence',
  kind: AIActionKind.Inline,
  icon: { iconName: 'Robot' },
  getLabel(t: TFunction<string[]>, params) {
    return getCriteriaLabel(t('criteria:Editor.AdviseEvidence'), params);
  },
  getAnchor(params) {
    return getCriteriaAnchor(params);
  },
  run(params) {
    params.run();
  },
};
